import { z } from 'zod';

import { apiClient } from '@/lib/api/client';
import { endpoints } from '@/lib/api/endpoints';

import { installerOptionSchema } from '../schemas';

import { toListQuery } from './filters';
import {
  replacementCategorySchema,
  replacementDetailSchema,
  replacementListResponseSchema,
  replacementOptionPageSchema,
} from './schemas';
import type {
  ApproveReplacementBody,
  RejectReplacementValues,
  ReplacementCategory,
  ReplacementDetail,
  ReplacementListParams,
  ReplacementListResponse,
  ReplacementOptionPage,
  ReplacementOptionParams,
} from './types';

/**
 * A column whose `dataIndex` is not the backend's own `sortBy` token.
 *
 * `<DataTable>` puts antd's `SorterResult.field` — i.e. the column's `dataIndex` — straight on the
 * wire, so every other column here is named for its token by coincidence of it matching. The
 * Replacement Request Date column cannot be: `replacementDate` is the field the row actually
 * carries and renders, while the backend's `sortBy` enum knows it as `createdAt`. Sending the
 * former 400'd with `VALIDATION_FAILED` on every click of that header (ADO 118732).
 *
 * Translated here rather than by renaming the column's `dataIndex`, so the column keeps pointing at
 * the value it displays and the wire token stays a property of the request.
 */
const SORT_BY_ALIASES: Record<string, string> = { replacementDate: 'createdAt' };

/** Sort + filters → the list's wire params, defaults included. The export sends the same, so the
 *  file comes out with the rows and order on screen, sorted or not (ADO 120974). */
export function replacementsRequestQuery(params: Omit<ReplacementListParams, 'page' | 'limit'>) {
  const { sortBy = 'createdAt', sortOrder = 'desc', ...filters } = params;
  return {
    sortBy: SORT_BY_ALIASES[sortBy] ?? sortBy,
    sortOrder: sortOrder.toUpperCase(),
    ...toListQuery(filters),
  };
}

/** Paginated/sorted/filtered/searchable replacement-request list. Defaults to most-recent
 *  Replacement Request Date first (real backend has no `requestedAt`; the closest — and
 *  default — sort column is `createdAt`). Real backend: `POST .../query` with a JSON body. */
export async function listReplacements(
  params: ReplacementListParams,
): Promise<ReplacementListResponse> {
  const { page, limit, ...rest } = params;
  const { data, meta } = await apiClient.postList(endpoints.requests.replacements.query, {
    page,
    limit,
    ...replacementsRequestQuery(rest),
  });
  return replacementListResponseSchema.parse({ data, meta });
}

/** Full replacement-request detail (`ReplacementRequestDetailResponse`) — item/claim-centric;
 *  the queue page merges this with the row it already has for customer/order/date context. */
export async function getReplacement(id: string): Promise<ReplacementDetail> {
  return replacementDetailSchema.parse(
    await apiClient.get(endpoints.requests.replacements.detail(id)),
  );
}

/** Rejects a replacement request with an admin-entered reason. Real backend: `PATCH`, returning
 *  only `{id, status}` — the caller invalidates rather than seeds the detail cache. */
export async function rejectReplacement(
  id: string,
  values: RejectReplacementValues,
): Promise<void> {
  await apiClient.patch(endpoints.requests.replacements.reject(id), values);
}

/** Approves a replacement request, optionally creating an installation request for the new
 *  $0.00 order. Real backend: `PATCH`, returning a slim `ApproveReplacementResponse` — the
 *  caller invalidates rather than seeds the detail cache. */
export async function approveReplacement(id: string, body: ApproveReplacementBody): Promise<void> {
  await apiClient.patch(endpoints.requests.replacements.approve(id), body);
}

/** Active field-installer directory scoped to Replacements' own approve-with-installation flow
 *  (`/requests/admin/replacements/meta/field-installers`) — same flat/unpaginated shape as the
 *  Installations sibling, but its own endpoint. */
export async function listReplacementFieldInstallers(search: string) {
  return z.array(installerOptionSchema).parse(
    await apiClient.get(endpoints.requests.replacements.meta.fieldInstallers, {
      params: search ? { search } : undefined,
    }),
  );
}

/**
 * Distinct cities present on replacement requests (reuses the Orders city meta endpoint,
 * BE-confirmed 114698). Still a flat string list — cities are name-based, not id-based.
 *
 * `hasReplacementRequest` and `isShipping` are the two params the BE named on ADO 118733, and both
 * must be sent: the endpoint is orders-wide by default, so without the former the dropdown offered
 * every shipping city in the system and picking one that no replacement request carries left the
 * table empty. Verified live 2026-09-22 — 11 cities unscoped, 4 scoped, and the extras (`Chicago`)
 * genuinely return zero rows. The scoped list is also the only one whose casing the listing
 * matches (`nashville` returns rows, `Nashville` — the unscoped spelling — returns none).
 */
export async function listReplacementCities(): Promise<string[]> {
  const data = await apiClient.get<string[]>(endpoints.requests.replacements.meta.cities, {
    params: { isShipping: true, hasReplacementRequest: true },
  });
  return data.map(String);
}

/** Device Category filter options (reuses the shared products-admin category meta endpoint,
 *  BE-confirmed 114698) — id-based, mirroring the Products/Devices category filter. */
export async function listReplacementCategories(): Promise<ReplacementCategory[]> {
  const data = await apiClient.get(endpoints.requests.replacements.meta.categories, {
    // Server-side, for the reason ADO 113384 records on the Products/Devices twins: the item is
    // `{ id, name }` with no `status`, so an inactive master Category cannot be filtered out here.
    // This is the one meta endpoint that takes **no** `hasReplacementRequest` — it 400s with
    // `property hasReplacementRequest should not exist` (verified live 2026-09-22), and the BE's
    // own note on ADO 118733 lists only the customer/device/city three.
    params: { limit: 100, status: 'active' },
  });
  return replacementCategorySchema.array().parse(data);
}

/** One page of the server-searched customer typeahead (cursor-paginated; reuses the shared
 *  products-admin customer meta endpoint Orders already consumes). */
export async function listReplacementCustomers({
  search,
  cursor,
  limit,
}: ReplacementOptionParams): Promise<ReplacementOptionPage> {
  const page = await apiClient.getList(endpoints.requests.replacements.meta.customers, {
    // Scoped to customers who actually have a replacement request (ADO 118733). Unscoped the
    // dropdown listed every customer in the system; live check 2026-09-22: 100+ → 4.
    params: { search, limit, hasReplacementRequest: true, ...(cursor ? { cursor } : {}) },
  });
  return replacementOptionPageSchema.parse({ ...page, meta: page.meta ?? { hasNextPage: false } });
}

/** One page of the server-searched device typeahead (cursor-paginated; reuses the shared
 *  products-admin device meta endpoint). */
export async function listReplacementDevices({
  search,
  cursor,
  limit,
}: ReplacementOptionParams): Promise<ReplacementOptionPage> {
  const page = await apiClient.getList(endpoints.requests.replacements.meta.devices, {
    // Scoped to devices that actually appear on a replacement request (ADO 118733); live check
    // 2026-09-22: 32 → 6. Deliberately *not* the bundle picker's `bundleEligible`/`status` pair,
    // which is the opposite mistake ADO 118533 fixed on the Warranty sibling.
    params: { search, limit, hasReplacementRequest: true, ...(cursor ? { cursor } : {}) },
  });
  return replacementOptionPageSchema.parse({ ...page, meta: page.meta ?? { hasNextPage: false } });
}
